import { ApiRequest } from '@sancare/typescript-commons/src/misc/api-request'
import dayjs from '@sancare/typescript-commons/src/misc/dayjs'
import { MedicalUnit } from '@sancare/typescript-commons/src/store/modules/remote-lists/types'
import { AbstractState } from '@sancare/typescript-commons/src/store/types'
import { HealthDataDocument } from '@sancare/typescript-commons/src/types/health-data'
import { Dayjs } from 'dayjs'

import type { SmrStaySuggestionItem } from '@/misc/types'
import { CodingSelection } from '@/stay-displayer/types'
import { Diagnosis } from '@/store/modules/diagnosis/types'
import { BaseAct, BaseCodedAct, CodedMedicalAct, MedicalAct, RawCodedMedicalAct } from '@/store/modules/medical-act/types'
import { User } from '@/store/modules/settings/types'
import { SmrGroupingResult } from '@/store/modules/stay/smr/grouping/types'
import { BaseSummaryUnit, RawStay, Stay } from '@/store/modules/stay/types'

export type SmrDiagnosis = Diagnosis & {
  canBeFpp?: boolean
  canBeMmp?: boolean
  canBeAe?: boolean
  canBeDas?: boolean
}

export type SmrAssociatedDiagnosis = {
  associatedDiagnosis: SmrDiagnosis
  id?: number
}

export type SmrDiagnosisGroup = {
  fpp: SmrAssociatedDiagnosis
  mmp: SmrAssociatedDiagnosis
  ae: SmrAssociatedDiagnosis
  dasList: SmrAssociatedDiagnosis[]
  groupingResult: SmrGroupingResult
}

export type RehabilitationAct = BaseAct
export type CodedRehabilitationAct = Pick<BaseCodedAct<unknown, RehabilitationAct>, 'id' | 'associatedAct' | 'disabled'>

export interface Dependencies {
  currentDepDressing: number
  currentDepMoving: number
  currentDepFeeding: number
  currentDepContinence: number
  currentDepBehaviour: number
  currentDepSocial: number
}

export type BaseRhs<TDate, TAct extends BaseAct, TCodedAct extends BaseCodedAct<TDate, TAct>>
  = BaseSummaryUnit<TDate, TAct, TCodedAct> & Dependencies & {
    medicalUnit: MedicalUnit
    weekDays: string
    weekEndDays: string
    initialModeDiagnosisGroup: SmrDiagnosisGroup
    currentModeDiagnosisGroup: SmrDiagnosisGroup
    codedRehabilitationActs: CodedRehabilitationAct[]
    admissionMode: string | null
    releaseMode: string | null
    origin: string | null
    destination: string | null
    // TODO: To be completed depending on serializer schema
  }

export type RhsRefreshedGroupingResult = {
  id: number
  stay: {
    id: number
    standardModeGroupingResult: SmrGroupingResult
  }
  currentModeDiagnosisGroup: SmrDiagnosisGroup
}

export type RawRhs = BaseRhs<string, MedicalAct, RawCodedMedicalAct> & {
  weekNb: string
}
export type Rhs = BaseRhs<Dayjs, MedicalAct, CodedMedicalAct> & {
  week: Week
}

interface Week {
  nb: number
  year: number
}

interface BaseSmrStay<TDate, TRhs> {
  smrId: number
  hospitalizationType: HospitalizationType
  rhsList: TRhs[]
  presenceDayCount: number
  staySuggestions?: SmrStaySuggestionItem[]
  firstStandardModeGroupingResult: SmrGroupingResult
  standardModeGroupingResult: SmrGroupingResult
  surgeryDate: TDate
  validationDate: string
  validationUser: Pick<User, 'id' | 'firstName' | 'lastName' | 'sancareUser'> | null
  // TODO: To be completed depending on serializer schema
}

export type RawSmrStay = RawStay & BaseSmrStay<string, RawRhs>
export type SmrStay = Stay & BaseSmrStay<Dayjs, Rhs>

// todo move in test data
export const defaultRhs = (): Rhs => {
  return {
    id: null,
    codedMedicalActs: [],
    codedRehabilitationActs: [],
    predictedLabels: [],
    medicalUnit: null,
    weekDays: '00000',
    endDate: dayjs(),
    startDate: dayjs(),
    week: {
      nb: 1,
      year: 0
    },
    weekEndDays: '00',
    currentDepDressing: 0,
    currentDepMoving: 0,
    currentDepFeeding: 0,
    currentDepContinence: 0,
    currentDepBehaviour: 0,
    currentDepSocial: 0,
    admissionMode: null,
    releaseMode: null,
    origin: null,
    destination: null,
    initialModeDiagnosisGroup: defaultSmrDiagnosisGroup(),
    currentModeDiagnosisGroup: defaultSmrDiagnosisGroup(),
  }
}

const defaultSmrDiagnosisGroup = (): SmrDiagnosisGroup => {
  return {
    fpp: defaultSmrDiagnosis(),
    mmp: defaultSmrDiagnosis(),
    ae: defaultSmrDiagnosis(),
    dasList: [],
    groupingResult: {
      gme: null,
      gmt: null,
      value: null,
    }
  }
}

const defaultSmrDiagnosis = (): SmrAssociatedDiagnosis => {
  return {
    associatedDiagnosis: {
      reference: null,
      canBeFpp: true,
      canBeMmp: true,
      canBeAe: true,
      canBeDas: true,
    }
  }
}

const baseUrl = '/api'

export class SmrStayState extends AbstractState {
  rawStay: RawSmrStay
  currentStay: SmrStay | null = null
  rawRhs: RawRhs
  currentRhs: Rhs | null = null
  mainContent: HealthDataDocument | null = null
  justificationSelection: CodingSelection | null = null
  scrollTarget: { docId: number, offset: number } | null = null
  isGroupingResultRefreshing: boolean = false

  constructor() {
    super()
    this.requests = {
      fetchStay: new ApiRequest('fetchStay', 'GET', `${baseUrl}/stays-smr/{idType}/{stayId}`),
      fetchRhs: new ApiRequest('fetchRhs', 'GET', `${baseUrl}/rhs/{id}`),
      updateStayStatus: new ApiRequest('updateStayStatus', 'POST', `${baseUrl}/stays/smr/update-status`),
      updateRhsDependency: new ApiRequest('updateRhsDependency', 'PUT', `${baseUrl}/rhs/{id}/dependency`),
      commentStay: new ApiRequest('commentStay', 'POST', `${baseUrl}/smr/stay/{id}/comment`),
      createFalseJustificationForSuggestion: new ApiRequest('createFalseJustificationForSuggestion', 'POST', `${baseUrl}/smr/stay/{stayId}/false-justification`),
      deleteFalseJustification: new ApiRequest('deleteFalseJustification', 'DELETE', `${baseUrl}/false-justification/{id}`),
    }
  }
}

export enum HospitalizationType {
  COMPLETE = 1,
  PARTIAL_DAY = 2,
  PARTIAL_NIGHT = 3,
  AMBULATORY = 4,
}

export enum SmrLoadingErrorReason {
  SMR_STAY_NOT_FOUND = 'SMR_STAY_NOT_FOUND',
  SMR_STAY_OTHER = 'SMR_STAY_OTHER'
}

export enum RhsLoadingErrorReason {
  RHS_NOT_FOUND = 'RHS_NOT_FOUND',
  RHS_OTHER = 'RHS_OTHER',
  RHS_UNKNOWN = 'RHS_UNKNOWN'
}

export enum RhsDashboardType {
  RHS_DASHBOARD_DIAGNOSES = 'RHS_DASHBOARD_DIAGNOSES',
  RHS_DASHBOARD_DEPENDENCIES = 'RHS_DASHBOARD_DEPENDENCIES',
  RHS_DASHBOARD_ACT_CCAM = 'RHS_DASHBOARD_ACT_CCAM',
  RHS_DASHBOARD_ACT_CSARR = 'RHS_DASHBOARD_ACT_CSARR'
}

export const RhsDashboardTitle = {
  [RhsDashboardType.RHS_DASHBOARD_DIAGNOSES]: 'Diagnostics CIM10',
  [RhsDashboardType.RHS_DASHBOARD_DEPENDENCIES]: 'Dépendances',
  [RhsDashboardType.RHS_DASHBOARD_ACT_CCAM]: 'Actes CCAM',
  [RhsDashboardType.RHS_DASHBOARD_ACT_CSARR]: 'Actes CSAR',
}

export const RhsDashboardClass = {
  [RhsDashboardType.RHS_DASHBOARD_DIAGNOSES]: 'dashboard-nosology',
  [RhsDashboardType.RHS_DASHBOARD_DEPENDENCIES]: 'dashboard-weight',
  [RhsDashboardType.RHS_DASHBOARD_ACT_CCAM]: 'dashboard-rehabilitation',
  [RhsDashboardType.RHS_DASHBOARD_ACT_CSARR]: 'dashboard-rehabilitation',
}

export const RhsDashboardButtonLabel = {
  [RhsDashboardType.RHS_DASHBOARD_DIAGNOSES]: 'Ajouter un diagnostic',
  [RhsDashboardType.RHS_DASHBOARD_ACT_CCAM]: 'Ajouter un acte',
  [RhsDashboardType.RHS_DASHBOARD_ACT_CSARR]: 'Ajouter un acte',
}


export enum DependenciesType {
  DRESSING = 'depDressing',
  MOVING = 'depMoving',
  FEEDING = 'depFeeding',
  CONTINENCE = 'depContinence',
  BEHAVIOR = 'depBehaviour',
  SOCIAL = 'depSocial',
  PHYSICAL = 'depPhysical',
  COGNITIVE = 'depCognitive',
}

export const DependenciesSections = {
  PHYSICAL: DependenciesType.PHYSICAL,
  COGNITIVE: DependenciesType.COGNITIVE,
}
export type DependenciesSections = typeof DependenciesSections[keyof typeof DependenciesSections]

export const DependenciesSectionsLabels = {
  [DependenciesSections.PHYSICAL]: 'Physique',
  [DependenciesSections.COGNITIVE]: 'Cognitif',
}

export const DependenciesLabels = {
  [DependenciesSections.PHYSICAL]: {
    [DependenciesType.DRESSING]: 'Habillage',
    [DependenciesType.MOVING]: 'Déplacement',
    [DependenciesType.FEEDING]: 'Alimentation',
    [DependenciesType.CONTINENCE]: 'Continence'
  },
  [DependenciesSections.COGNITIVE]: {
    [DependenciesType.BEHAVIOR]: 'Comportement',
    [DependenciesType.SOCIAL]: 'Communication'
  }
}

export type RhsDependencyElement = {
  dependencyType: string
  value: number
}

export interface SuggestedDependency {
  lowValue: string
  highValue: string
  formattedValue: string
}
